/**
 * Project scope for the object type and process pickers. Navigation only: it
 * narrows what a dropdown offers while a project is open; every service still
 * enforces authorization and no saved value changes.
 *
 * It builds on `process_v2/projectScope` (membership of `object_type`
 * resources in the open project). With no project open, or "Todos", every
 * helper returns its input untouched.
 *
 * Editing rule: a type the thing being edited already uses stays in the list
 * even when it lives outside the project, so the current value never turns
 * into a blank or a raw id.
 */
import { useMemo } from 'react';
import type { Process } from '../process_v2/api';
import { scopeObjectTypes, scopeProcesses, useProjectObjectTypeIds, type ProjectObjectTypeIds } from '../process_v2/projectScope';

export interface NamedObjectType { id: string; name?: string | null; displayName?: string | null; display_name?: string | null }

/** Ids of the types a list of references points at. A reference is an id or,
 *  for configs that store it, the type's name or display name. */
export function resolveObjectTypeRefs(all: ReadonlyArray<NamedObjectType>, refs: ReadonlyArray<string | null | undefined>): string[] {
  const wanted = new Set(refs.filter((r): r is string => typeof r === 'string' && r !== ''));
  if (!wanted.size) return [];
  const ids = all
    .filter((o) => wanted.has(o.id) || (!!o.name && wanted.has(o.name)) || (!!o.displayName && wanted.has(o.displayName)) || (!!o.display_name && wanted.has(o.display_name)))
    .map((o) => o.id);
  // A reference that matches nothing in `all` may still be an id of a type the
  // caller filtered out earlier; keep it so `scopeObjectTypes` can match it.
  return [...new Set([...ids, ...wanted])];
}

/** The project's object types plus the ones `refs` already point at. */
export function scopeObjectTypesKeeping<T extends NamedObjectType>(all: T[], ids: ProjectObjectTypeIds, refs: ReadonlyArray<string | null | undefined> = []): T[] {
  if (!ids) return all;
  return scopeObjectTypes(all, ids, resolveObjectTypeRefs(all, refs));
}

/** Object types a picker may offer: the open project's, plus `refs` (ids or names in use). */
export function useProjectObjectTypes<T extends NamedObjectType>(all: T[], refs: ReadonlyArray<string | null | undefined> = []): T[] {
  const ids = useProjectObjectTypeIds();
  const refsKey = refs.filter(Boolean).join('\u0001');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => scopeObjectTypesKeeping(all, ids, refs), [all, ids, refsKey]);
}

/**
 * Object types split for pickers that must not hide shared types: the open
 * project's (plus `refs` in use) first, then every other type the user can
 * already read. `all` is the list the ontology API returned for this user, so
 * nothing is granted here. With no project open, everything is `project`.
 */
export function splitObjectTypesByProject<T extends NamedObjectType>(
  all: T[], ids: ProjectObjectTypeIds, refs: ReadonlyArray<string | null | undefined> = [],
): { project: T[]; other: T[] } {
  if (!ids) return { project: all, other: [] };
  const mine = new Set(scopeObjectTypesKeeping(all, ids, refs).map((o) => o.id));
  return { project: all.filter((o) => mine.has(o.id)), other: all.filter((o) => !mine.has(o.id)) };
}

export function useProjectObjectTypeGroups<T extends NamedObjectType>(
  all: T[], refs: ReadonlyArray<string | null | undefined> = [],
): { project: T[]; other: T[] } {
  const ids = useProjectObjectTypeIds();
  const refsKey = refs.filter(Boolean).join('\u0001');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => splitObjectTypesByProject(all, ids, refs), [all, ids, refsKey]);
}

/** Processes a picker may offer: the open project's, plus `keepIds` (the one in use). */
export function useProjectProcesses<P extends Process>(all: P[], keepIds: ReadonlyArray<string | null | undefined> = []): P[] {
  const ids = useProjectObjectTypeIds();
  const keepKey = keepIds.filter(Boolean).join('\u0001');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => scopeProcesses(all, ids, keepIds.filter((k): k is string => !!k)) as P[], [all, ids, keepKey]);
}
